/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import { useDispatch } from 'react-redux';
import { Box, Center, FormLabel, Image, Input, Select, Stack } from '@chakra-ui/react';
import { Component, Components, getComponentImageFileCfn, getFuelSkuSuffix, Panel, ReferenceMap, Vertex2Data } from '@rta/rta-blueprint-library';
import { DisplayMode } from '@rta/rta-blueprint-component';
import { useEffect, useState, type JSX } from 'react';

import { calculatePriceDelta } from './helpers';
import { ContextPanelContent } from './context-panel-content';
import { formatPrice, isSleevePairing, resolvePanelItemGroup } from '@helpers';
import { lang } from '@lang';
import { setContextPanelIsOpen } from '@state';
import { useDesign } from '@hooks';

/**
 * Triggers auto-distribution on a panel if SKU layer is active
 * @param panel The panel to distribute items on
 * @param controller The controller to check SKU layer status
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const triggerAutoDistributionIfSkuLayerActive = (panel: Panel | null, controller: any): void => {
	if (!panel) return;

	const currentDisplayMode = controller?.navigation?.displayMode;
	let shouldDistribute = false;

	switch (currentDisplayMode) {
		case DisplayMode.SkuOverhead:
		case DisplayMode.SkuOverheadManufacturing:
			shouldDistribute = true;
			break;
		default:
			shouldDistribute = false;
			break;
	}

	if (shouldDistribute) {
		// Use setTimeout to ensure the item is fully added before distributing
		setTimeout(() => {
			panel.distributeApplianceGroupsHorizontally();
		}, 0);
	}
};

type AddAppliancePanelProps = {
	id: string | null;
	position?: Vertex2Data;
};

export const AddAppliancePanel: React.FC<AddAppliancePanelProps> = (props: AddAppliancePanelProps): JSX.Element => {
	const { id, position = { x: 0, y: 0 }} = props;

	const dispatch = useDispatch();
	const [controller] = useDesign();

	const kitchen = controller?.kitchen ?? null;
	const vendors = kitchen?.context.vendors;
	const categories = kitchen?.context.categories;
	const components = kitchen?.context.components ?? [];

	const [refMap, setRefMap] = useState<ReferenceMap | null>(null);
	const [vendorId, setVendorId] = useState('');
	const [categoryId, setCategoryId] = useState('');
	const [applianceList, setApplianceList] = useState<Array<Component>>([]);
	const [searchAppliance, setSearchAppliance] = useState('');
	const [spacerPlacement, setSpacerPlacement] = useState<string>(Components.PLACEMENT_TOP_ONLY);

	useEffect(() => {
		if (kitchen === null || id === null) return;

		// get the reference map for the provided id
		const map = kitchen?.getReferenceMap(id);
		if (map && map.id === id) {
			setRefMap(map);

			// default category and vendor to the current panel item, if passed
			if (map.panelItem) {
				setVendorId(map.panelItem.item.vendor.id);
				setCategoryId(map.panelItem.item.category.id);
			}
		}
	}, [kitchen, id]);

	useEffect(() => {
		if (refMap === null || refMap.panel === null) return;

		// get all appliances that are active and match the selected vendor and category
		const updatedList: Array<Component> = [];
		const { panel, panelItem } = refMap;

		// filter the list of appliances based on the selected vendor and category
		// note: array.filter didn't work here, so we're using a forEach loop
		components.forEach((component) => {
			const isCategoryActive = component.category?.active ?? false;
			const isVendorActive = component.vendor?.active ?? true;
			const isSkuActive = component.active ?? false;
			const isPlacementMatch = panelItem ? component.placement === panelItem.item.placement : true;
			const isVendorMatch = !vendorId || component.vendor?.id === vendorId;
			const isCategoryMatch = !categoryId || component.category?.id === categoryId;
			// A fuel appliance has one row per fuel; only the kitchen's own fuel is listed, so the
			// picker shows one entry per appliance with the sku and price that will actually be placed.
			const isFuelMatch = !component.fuelFamily || component.fuelType === getFuelSkuSuffix(kitchen?.fuelchoice);
			// Search matches on description, sku, and label for easier identification
			const isSearchMatch = !searchAppliance || component.description.toLowerCase().includes(searchAppliance.toLowerCase()) || component.sku.toLowerCase().includes(searchAppliance.toLowerCase()) || component.label.toLowerCase().includes(searchAppliance.toLowerCase());
			const showSkuItem = isSkuActive && isCategoryActive && isVendorActive && isPlacementMatch && isVendorMatch && isCategoryMatch && isSearchMatch && isFuelMatch;
			if (showSkuItem) updatedList.push(component);
		});

		setApplianceList(updatedList);
	}, [vendorId, categoryId, refMap, searchAppliance, kitchen?.fuelchoice]);


	if (!kitchen || !refMap || !refMap.panel) return <></>;
	// console.log('render:AddAppliancePanel', id, position, refMap.panel, refMap.panelItem);

	const title = refMap.panelItem ? lang.contextPanels.title.swapAppliance : lang.contextPanels.title.addAppliance;

	return <ContextPanelContent title={title}>
		<Stack spacing="12px" p="5px" width="100%">
			<Box>
				<FormLabel htmlFor="vendor-filter" fontSize="small">Vendor :</FormLabel>
				<Select id="vendor-filter" placeholder="All" fontSize="small"
					value={vendorId}
					onChange={(e) => { setVendorId(e.target.value); }}
				>
					{vendors && vendors
						.filter((v) => v.active)
						.map((v, i) => <option key={i} value={v.id}>{v.displayValue}</option>)
					}
				</Select>
			</Box>
			<Box>
				<FormLabel htmlFor="category-filter" fontSize="small">Category :</FormLabel>
				<Select id="category-filter" placeholder="All" fontSize="small"
					value={categoryId}
					onChange={(e) => { setCategoryId(e.target.value); }}
				>
					{categories && categories.getActiveEntries()
						.map((c, i) => <option key={i} value={c.id}>{c.description}</option>)
					}
				</Select>
			</Box>
			<Box>
				<FormLabel htmlFor="appliance-search" fontSize="small">Search :</FormLabel>
				<Input id="appliance-search" placeholder="Search Appliance" fontSize="small" size="sm" value={searchAppliance} onChange={(e) => { console.log(e.target.value); setSearchAppliance(e.target.value); }} />
			</Box>
			{ applianceList.map((skuItem, i) => <Box key={i} maxW="sm" borderWidth="1px" borderRadius="lg" overflow="hidden" p="2">
				<Center key={i}>{
					skuItem.category?.key === 'spacer'
						? <Box w="100%" display="flex" flexDirection="column" alignItems="center" gap="2">
							<Box
								h="60px" w="80%" display="flex" alignItems="center" justifyContent="center"
								border="2px dashed" borderColor="gray.400" borderRadius="md" color="gray.500"
								fontSize="xs" fontWeight="medium" cursor="pointer"
								onDoubleClick={() => {
									let newPanelItem = null;
									const isSpaceReservation = skuItem.sku === 'RTA-SPACE-RESERVATION';
									const placement = isSpaceReservation ? spacerPlacement : skuItem.placement;
									const targetGroup = resolvePanelItemGroup({ refMap, placement, fallbackX: position.x, incoming: skuItem });

									if (targetGroup) {
										newPanelItem = targetGroup.items.newFromId(skuItem.id);
										if (targetGroup === refMap.panelItemGroup) {
											// sleeve + occupant (asado/grill/side-burner) pair inside one group;
											// preserving the existing item lets getPanelSleeveCfn resolve the pairing on read.
											if (refMap.panelItem && !isSleevePairing(refMap.panelItem, skuItem)) {
												newPanelItem.center.y = refMap.panelItem.center.y;
												targetGroup.items.delete(refMap.panelItem.id);
											} else if (typeof skuItem.defaultPlacement?.top !== 'number' && typeof skuItem.defaultPlacement?.bottom !== 'number') {
												// no catalog-declared vertical position, so the click point is the starting y
												newPanelItem.center.y = -position.y;
											}
										}
										targetGroup.centerItemsVertically();
										triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
									}
									if (newPanelItem && isSpaceReservation) {
										if (!newPanelItem.item.override) {
											newPanelItem.item.override = true;
										}
										newPanelItem.item.component.placement = spacerPlacement;
									}
									dispatch(setContextPanelIsOpen(false));
								}}
							>Space Reservation</Box>
							{skuItem.sku === 'RTA-SPACE-RESERVATION' && (
								<Select size="xs" w="80%" value={spacerPlacement} onChange={(e) => setSpacerPlacement(e.target.value)}>
									<option value={Components.PLACEMENT_FRONT_ONLY}>Front Only</option>
									<option value={Components.PLACEMENT_TOP_ONLY}>Top Only</option>
								</Select>
							)}
						</Box>
					: skuItem.svg?.item
						? <svg
							height="100px"
							viewBox={`0 0 ${skuItem.size.width} ${skuItem.size.height}`}
							onDoubleClick={() => {
								const posX = refMap.panel?.isBackPanel ? -position.x : position.x;
								const targetGroup = resolvePanelItemGroup({ refMap, placement: skuItem.placement, fallbackX: posX, incoming: skuItem });

								if (targetGroup) {
									const panelItem = targetGroup.items.newFromId(skuItem.id);
									if (targetGroup === refMap.panelItemGroup) {
										// if refMap is PanelItem then we are in swap, delete old item -- unless the
										// two items pair as sleeve + occupant, in which case both belong in the group.
										if (refMap.panelItem && !isSleevePairing(refMap.panelItem, skuItem)) {
											panelItem.center.y = refMap.panelItem.center.y;
											targetGroup.items.delete(refMap.panelItem.id);
										} else if (typeof skuItem.defaultPlacement?.top !== 'number' && typeof skuItem.defaultPlacement?.bottom !== 'number') {
											// no catalog-declared vertical position, so the click point is the starting y
											panelItem.center.y = -position.y;
										}
									}
									targetGroup.centerItemsVertically();
									// Auto-distribute if SKU layer is active
									triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
								}

								// Close Drawer
								dispatch(setContextPanelIsOpen(false));
							}}
						>{ skuItem.svg.item({ x: 0, y: 0, diameter: skuItem.cutout.width })}</svg>
						: <Image alt={skuItem.sku}
							src={`${import.meta.env.VITE_ASSETS_BASE_URL}/products/${getComponentImageFileCfn(skuItem)}`}
							h="100px"
							align="center"
							onDoubleClick={() => {
								const posX = refMap.panel?.isBackPanel ? -position.x : position.x;
								const targetGroup = resolvePanelItemGroup({ refMap, placement: skuItem.placement, fallbackX: posX, incoming: skuItem });

								if (targetGroup) {
									const panelItem = targetGroup.items.newFromId(skuItem.id);
									if (targetGroup === refMap.panelItemGroup) {
										// if refMap is PanelItem then we are in swap, delete old item -- unless the
										// two items pair as sleeve + occupant, in which case both belong in the group.
										if (refMap.panelItem && !isSleevePairing(refMap.panelItem, skuItem)) {
											panelItem.center.y = refMap.panelItem.center.y;
											targetGroup.items.delete(refMap.panelItem.id);
										} else if (typeof skuItem.defaultPlacement?.top !== 'number' && typeof skuItem.defaultPlacement?.bottom !== 'number') {
											// no catalog-declared vertical position, so the click point is the starting y
											panelItem.center.y = -position.y;
										}
									}
									targetGroup.centerItemsVertically();
									// Auto-distribute if SKU layer is active
									triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
								}
								// Close Drawer
								dispatch(setContextPanelIsOpen(false));
							}}
						/>
				}</Center>
				<Box p="2">
					<Box
						color="blue.400"
						fontWeight="semibold"
						letterSpacing="wide"
						fontSize="s"
						textTransform="uppercase"
						ml="1">
						{skuItem.displayName}
					</Box>
					<Box
						color="gray.500"
						fontWeight="semibold"
						letterSpacing="wide"
						fontSize="xs"
						textTransform="uppercase"
						ml="2">
						{skuItem.sku}
					</Box>
					<Box
						color="gray.500"
						fontWeight="semibold"
						letterSpacing="wide"
						fontSize="xs"
						textTransform="uppercase"
						ml="2">
						w {skuItem.size.width}{'"'} / h {skuItem.size.height}{'"'} / d {skuItem.size.depth}{'"'}
					</Box>
					<Box
						color="gray.500"
						fontWeight="semibold"
						letterSpacing="wide"
						fontSize="xs"
						textTransform="uppercase"
						ml="2">
						{formatPrice(skuItem.msrp)} { refMap.panelItem && (
							(() => {
								const { delta, color } = calculatePriceDelta(skuItem.msrp, refMap.panelItem.item.msrp);
								const formattedDelta = Math.abs(delta);
								const sign = delta > 0 ? '+' : '-';
								const isCurrentItem = skuItem.id === refMap.panelItem.item.id;
								return (
									<span style={{ color, marginLeft: '4px' }}>
											[{isCurrentItem ? 'current item' : `${sign}${formatPrice(formattedDelta)}`}]
									</span>
								);
							})()
						)}
					</Box>
					<Box
						mt="1"
						fontWeight="semibold"
						as="h4"
						lineHeight="tight"
					>
						{skuItem.description}
					</Box>
				</Box>
			</Box>)}
		</Stack>
	</ContextPanelContent>;
};
